import { DynamicPageConfig, TemplateType } from 'compobot-dynamic-page';
import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { getCompobotGuiVersionById } from 'services/compobotGuiVersions.service';
import {
  CompobotGuiVersionType,
  CreateGuiVersionType,
} from 'types/compobotGuiVersion.type';
import { defaultColors } from './defaultValues';
import { populateFormFromData } from './formUtils';
import {
  createDefaultPreviewConfig,
  updatePreviewConfig,
} from './previewConfig';

type ColorState = {
  pretext: string;
  title: string;
  subtitle: string;
  overlay: string;
};

export const useGuiVersionForm = (id?: string) => {
  // State management
  const [data, setData] = useState<CompobotGuiVersionType | null>(null);
  const [pretextEnCount, setPretextEnCount] = useState(0);
  const [pretextHuCount, setPretextHuCount] = useState(0);
  const [titleEnCount, setTitleEnCount] = useState(0);
  const [titleHuCount, setTitleHuCount] = useState(0);
  const [subtitleEnCount, setSubtitleEnCount] = useState(0);
  const [subtitleHuCount, setSubtitleHuCount] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>(
    TemplateType.THEME_01,
  );
  const [useCustomPageParams, setUseCustomPageParams] = useState(true);
  const [colors, setColors] = useState<ColorState>(defaultColors);
  const [previewConfig, setPreviewConfig] = useState<DynamicPageConfig>(
    createDefaultPreviewConfig(),
  );

  // Load data effect
  useEffect(() => {
    if (id) {
      getCompobotGuiVersionById(id).then(({ data }) => {
        setData(data);
      });
    }
  }, [id]);

  // Populate form effect
  const populateForm = (setValue: UseFormSetValue<CreateGuiVersionType>) => {
    useEffect(() => {
      if (data) {
        populateFormFromData(data, setValue, setColors, {
          setPretextEnCount,
          setPretextHuCount,
          setTitleEnCount,
          setTitleHuCount,
          setSubtitleEnCount,
          setSubtitleHuCount,
        });
      }
    }, [data, setValue]);
  };

  // Update preview effect
  const updatePreview = (watchedValues: CreateGuiVersionType) => {
    useEffect(() => {
      if (watchedValues) {
        setPreviewConfig(updatePreviewConfig(watchedValues, colors));

        // Update selected template
        if (watchedValues.templateType) {
          setSelectedTemplate(watchedValues.templateType as TemplateType);
        }
      }
    }, [watchedValues, colors]);
  };

  // Color change handlers
  const colorHandlers = {
    onPretextColorChange: (color: string) =>
      setColors((prev: ColorState) => ({ ...prev, pretext: color })),
    onTitleColorChange: (color: string) =>
      setColors((prev: ColorState) => ({ ...prev, title: color })),
    onSubtitleColorChange: (color: string) =>
      setColors((prev: ColorState) => ({ ...prev, subtitle: color })),
    onOverlayColorChange: (color: string) =>
      setColors((prev: ColorState) => ({ ...prev, overlay: color })),
  };

  return {
    // State
    data,
    colors,
    previewConfig,
    selectedTemplate,
    useCustomPageParams,
    setUseCustomPageParams,

    // Word counts
    pretextEnCount,
    pretextHuCount,
    setPretextEnCount,
    setPretextHuCount,
    titleEnCount,
    titleHuCount,
    setTitleEnCount,
    setTitleHuCount,
    subtitleEnCount,
    subtitleHuCount,
    setSubtitleEnCount,
    setSubtitleHuCount,

    // Effects
    populateForm,
    updatePreview,

    // Handlers
    ...colorHandlers,
  };
};
